<template>
  <div>
    <premium-subscribers-card />

    <div class="table-header">
      <div class="search-container">
        <v-text-field
          v-model="searchQuery"
          placeholder="Search users..."
          dense
          outlined
          clearable
          class="search-input"
          @input="handleSearch"
        >
          <template v-slot:prepend-inner>
            <v-icon>mdi-magnify</v-icon>
          </template>
        </v-text-field>
      </div>
      <select
        v-model="selectedEventType"
        class="event-type-select"
        @change="handleEventTypeChange"
      >
        <option
          v-for="option in eventTypeOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.text }}
        </option>
      </select>
      <select
        v-if="selectedEventType === EVENT_TYPES.WORKOUT_COMPLETED"
        v-model="selectedWorkoutType"
        class="workout-type-select"
        @change="handleWorkoutTypeChange"
      >
        <option
          v-for="option in workoutTypeOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.text }}
        </option>
      </select>
      <date-range-picker
        v-model="tableDateRange"
        :single-date-picker="false"
        :ranges="ranges"
        opens="right"
        :auto-apply="true"
        :min-date="new Date('03-10-2025')"
        :max-date="new Date()"
        @update="getTableData"
        @clickRange="onRangeClick"
        control-container-class="table-date-range"
      >
        <template v-slot:input="">
          <div class="table-date-range">
            {{ tableDateRange.startDate.toLocaleDateString() }} -
            {{ tableDateRange.endDate.toLocaleDateString() }}
          </div>
        </template>
      </date-range-picker>
      <v-btn class="refresh-button" icon @click="resetAndRefresh">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <div class="pagination-controls">
      <v-btn
        class="pagination-button"
        icon
        :disabled="currentPage <= 1"
        @click="changePage(currentPage - 1)"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <span class="page-info"
        >Page {{ currentPage }} of
        {{ Math.ceil(filteredEvents.length / itemsPerPage) || 1 }}</span
      >
      <v-btn
        class="pagination-button"
        icon
        :disabled="!hasMorePages"
        @click="changePage(currentPage + 1)"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </div>
    <v-data-table
      :headers="eventsHeaders"
      :items="inAppEvents"
      hide-default-footer
      hide-default-header
      class="elevation-4"
      :items-per-page="100"
      :loading="loading"
    >
      <template v-slot:top>
        <div class="table-title">
          <span
            >{{ selectedEventType === EVENT_TYPES.ACTIVE_USERS ? 'Active Users' : 'In App Events' }}
            <span class="events-count">
              ({{ searchQuery ? filteredEvents.length : totalEvents }})
            </span></span
          >

          <div class="download-icon-container" @click="downloadCSV">
            <v-icon class="download-icon-2" color="primary"
              >mdi-download-box-outline</v-icon
            >Download CSV
          </div>
        </div>
        <div
          class="event-type-counts"
          v-if="
            [
              EVENT_TYPES.WORKOUT_STARTED,
              EVENT_TYPES.WORKOUT_PAUSED,
              EVENT_TYPES.WORKOUT_RESUMED,
              EVENT_TYPES.WORKOUT_COMPLETED,
            ].includes(selectedEventType) && !selectedWorkoutType
          "
        >
          <div class="workout-type-counts">
            <div class="workout-type-count">
              <span class="count-label">Coached Class:</span>
              <span class="count-value">{{
                loading ? "-" : getWorkoutTypeCount("coachedClass")
              }}</span>
            </div>
            <div class="workout-type-count">
              <span class="count-label">Live:</span>
              <span class="count-value">{{
                loading ? "-" : getWorkoutTypeCount("live")
              }}</span>
            </div>
            <div class="workout-type-count">
              <span class="count-label">Pre Set:</span>
              <span class="count-value">{{
                loading ? "-" : getWorkoutTypeCount("preSet")
              }}</span>
            </div>
            <div class="workout-type-count">
              <span class="count-label">Freestyle:</span>
              <span class="count-value">{{
                loading ? "-" : getWorkoutTypeCount("Freestyle")
              }}</span>
            </div>
            <div class="workout-type-count">
              <span class="count-label">BYO:</span>
              <span class="count-value">{{
                loading ? "-" : getWorkoutTypeCount("byo")
              }}</span>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:[`item.text`]="{ item }">
        <event-row
          :openErrorModal="openErrorModal"
          :item="item"
          :openPostModal="openPostModal"
          :openResultModal="openResultModal"
          :openUserModal="openUserPreviewModal"
          :openWorkoutModal="openWorkoutModal"
        />
      </template>
    </v-data-table>
    <user-preview-modal
      :show="showUserPreviewModal"
      :item="selectedUser"
      :close="closeUserPreviewModal"
    />
    <workout-modal
      :show="showWorkoutModal"
      :workoutId="selectedWorkoutId"
      :workoutType="selectedWorkoutType"
      :userId="selectedUserId"
      :close="closeWorkoutModal"
    />
  </div>
</template>

<script>
import EventRow from "@/components/admin/events/EventRow.vue";
import TrendsCard from "@/components/admin/events/TrendsCard.vue";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import moment from "moment";
import { EVENT_TYPES } from "@/constants/eventTypes";
import UserPreviewModal from "@/components/admin/users/userPreviewModal.vue";
import WorkoutModal from "./WorkoutModal.vue";
import PremiumSubscribersCard from "@/components/admin/events/PremiumSubscribersCard.vue";

export default {
  name: "EventsSection",
  components: {
    EventRow,
    DateRangePicker,
    TrendsCard,
    UserPreviewModal,
    WorkoutModal,
    PremiumSubscribersCard,
  },
  props: {
    openErrorModal: {
      type: Function,
      required: true,
    },
    openPostModal: {
      type: Function,
      required: true,
    },
    openResultModal: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    inAppEvents: [],
    originalEvents: [],
    eventsHeaders: [
      {
        text: "In App Events",
        align: "start",
        value: "text",
        sortable: false,
      },
    ],
    tableDateRange: {
      startDate: moment().startOf("day").toDate(),
      endDate: moment().endOf("day").toDate(),
    },
    ranges: {},
    currentPage: 1,
    itemsPerPage: 100,
    hasMorePages: false,
    selectedEventType: null,
    selectedWorkoutType: null,
    EVENT_TYPES,
    eventTypeOptions: [
      { text: "All Events", value: null },
      ...Object.entries(EVENT_TYPES)
        .filter(([, value]) => value !== EVENT_TYPES.AVG_WORKOUT_TIME)
        .map(([key, value]) => {
        const labelMap = {
          Login: "Login",
          SingUp: "Sign Up",
          "Workout created by user": "Workout Created",
          "Worout shared by user": "Workout Shared",
          "Workout started": "Workout Started",
          "Workout paused": "Workout Paused",
          "Workout resumend": "Workout Resumed",
          "Workout completed": "Workout Completed",
          "Post created": "Post Created",
          "Post shared": "Post Shared",
          "Post comment": "Post Comment",
          "Post pump": "Post Pump",
          "Subscription creadted": "Subscription Created",
          "Program join": "Program Join",
          "Program completed": "Program Completed",
          "IAChat message sent": "IAChat Message Sent",
          "App opened": "App Opened",
          "Active users": "Active Users",
        };
        return {
          text: labelMap[value] || value,
          value: value,
        };
      }),
    ],
    workoutTypeOptions: [
      { text: "All Workout Types", value: null },
      { text: "Coached Class", value: "coachedClass" },
      { text: "Live", value: "live" },
      { text: "Pre Set", value: "preSet" },
      { text: "Freestyle", value: "Freestyle" },
      { text: "BYO", value: "byo" },
    ],
    totalEvents: 0,
    searchQuery: "",
    loading: false,
    showUserPreviewModal: false,
    selectedUser: null,
    showWorkoutModal: false,
    selectedWorkoutId: "",
    selectedUserId: null,
  }),
  created() {
    const today = moment().clone();
    const yesterday = today.clone().subtract(1, "day");
    const last7Days = today.clone().subtract(7, "days");
    const last30Days = today.clone().subtract(30, "days");

    this.ranges = {
      Today: [today.startOf("day").toDate(), today.endOf("day").toDate()],
      Yesterday: [
        yesterday.startOf("day").toDate(),
        yesterday.endOf("day").toDate(),
      ],
      "Last 7 days": [
        last7Days.startOf("day").toDate(),
        today.endOf("day").toDate(),
      ],
      "Last 30 days": [
        last30Days.startOf("day").toDate(),
        today.endOf("day").toDate(),
      ],
    };

    this.getTableData();
  },
  methods: {
    onRangeClick(dateRange) {
      // When a range is clicked, manually set the date range
      if (dateRange && dateRange.length === 2) {
        this.tableDateRange = {
          startDate: dateRange[0],
          endDate: dateRange[1],
        };
        this.getTableData(this.tableDateRange);
      }
    },
    openUserPreviewModal(user) {
      if (!user) {
        console.warn(
          "Attempted to open user preview modal with null user data"
        );
        return;
      }
      this.selectedUser = user;
      this.showUserPreviewModal = true;
    },
    closeUserPreviewModal() {
      this.showUserPreviewModal = false;
      this.selectedUser = null;
    },
    async handleEventTypeChange() {
      this.currentPage = 1;
      this.hasMorePages = false;
      this.selectedWorkoutType = null;
      await this.getTableData();
    },
    async handleWorkoutTypeChange() {
      this.currentPage = 1;
      this.hasMorePages = false;
      await this.getTableData();
    },
    handleSearch() {
      this.currentPage = 1;
      this.inAppEvents = this.paginatedEvents;
      this.hasMorePages = this.filteredEvents.length > this.itemsPerPage;
    },
    getEventTypeCount(type) {
      const count = this.filteredEvents.filter((event) => {
        return event.eventType === type;
      }).length;
      return count;
    },
    getWorkoutTypeCount(type) {
      return this.filteredEvents.filter((event) => {
        return (
          [
            EVENT_TYPES.WORKOUT_STARTED,
            EVENT_TYPES.WORKOUT_PAUSED,
            EVENT_TYPES.WORKOUT_RESUMED,
            EVENT_TYPES.WORKOUT_COMPLETED,
          ].includes(event.eventType) && event.type === type
        );
      }).length;
    },
    async getTableData(newDates) {
      this.inAppEvents = [];
      this.originalEvents = [];
      this.currentPage = 1;
      this.loading = true;

      const startDate = newDates
        ? moment(newDates.startDate).startOf("day").toDate()
        : moment(this.tableDateRange.startDate).startOf("day").toDate();
      const endDate = newDates
        ? moment(newDates.endDate).endOf("day").toDate()
        : moment(this.tableDateRange.endDate).endOf("day").toDate();

      try {
        // Check if Active Users is selected
        if (this.selectedEventType === EVENT_TYPES.ACTIVE_USERS) {
          const users = await this.$store.dispatch("getActiveUsersList", {
            startDate,
            endDate,
          });
          
          // Transform user data into event-like format for display
          this.originalEvents = users.map((user) => {
            // Use lastLogin if available, otherwise use current date
            const createdAt = user.lastLogin;
            
            return {
              id: user.id,
              eventType: EVENT_TYPES.ACTIVE_USERS,
              user: user,
              createdAt: {
                toDate: () => createdAt
              },
            };
          });
        } else {
          const res = await this.$store.dispatch("getEvents", {
            startDate,
            endDate,
            type: this.selectedEventType,
            workoutType: this.selectedWorkoutType,
            populateUser: true,
          });
          this.originalEvents = Array.isArray(res) ? res : [];
        }
        
        this.inAppEvents = this.paginatedEvents;
        this.hasMorePages = this.filteredEvents.length > this.itemsPerPage;
        this.totalEvents = this.originalEvents.length;
      } catch (error) {
        console.error("Error fetching events:", error);
        this.originalEvents = [];
        this.inAppEvents = [];
        this.hasMorePages = false;
        this.totalEvents = 0;
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.inAppEvents = this.paginatedEvents;
    },
    resetAndRefresh() {
      this.tableDateRange = {
        startDate: moment().startOf("day").toDate(),
        endDate: moment().endOf("day").toDate(),
      };
      this.currentPage = 1;
      this.hasMorePages = false;
      this.selectedEventType = null;
      this.selectedWorkoutType = null;
      this.searchQuery = "";
      this.getTableData();
    },
    openWorkoutModal(workoutId, type, userId) {
      this.selectedWorkoutId = workoutId;
      this.selectedWorkoutType = type;
      this.selectedUserId = userId;
      this.showWorkoutModal = true;
    },
    closeWorkoutModal() {
      this.showWorkoutModal = false;
      this.selectedWorkoutId = "";
      this.selectedWorkoutType = null;
      this.selectedUserId = null;
    },
    getUserName(item) {
      const user = item.user;
      if (user.nickname && user.nickname !== "") {
        return user.nickname;
      }
      if (user.displayName && user.displayName !== "") {
        return user.displayName;
      }

      if (
        item.eventType === EVENT_TYPES.LOGIN ||
        item.eventType === EVENT_TYPES.SING_UP
      ) {
        if (user.email && user.email !== "") {
          return user.email;
        }
      }

      return `${user.firstName} ${user.lastName}`;
    },
    getWorkoutName(event, item) {
      switch (event.type) {
        case "coachedClass":
          return item.name || "Coached Class";
        case "live":
          return item.title?.ENG || "Live";
        case "byo":
          return item.name || "BYO";
        case "Freestyle":
          return item.name || "FreeStyle";
        case "preSet":
          return item.name?.ENG || "Pre Set";
        default:
          return "Workout";
      }
    },
    async downloadCSV() {
      const labelMap = {
        Login: "Login",
        SingUp: "Sign Up",
        "Workout created by user": "Workout Created",
        "Worout shared by user": "Workout Shared",
        "Workout started": "Workout Started",
        "Workout paused": "Workout Paused",
        "Workout resumend": "Workout Resumed",
        "Workout completed": "Workout Completed",
        "Post created": "Post Created",
        "Post shared": "Post Shared",
        "Post comment": "Post Comment",
        "Post pump": "Post Pump",
        "Subscription creadted": "Subscription Created",
        "Program join": "Program Join",
        "Program completed": "Program Completed",
        "IAChat message sent": "IAChat Message Sent",
        "App opened": "App Opened",
        "Active users": "Active User",
      };

      const csvData = await Promise.all(
        this.originalEvents.map(async (event) => {
          let workoutName = "";
          if (event.workoutId) {
            const workout = await this.$store.dispatch("getWorkoutData", {
              workoutId: event.workoutId,
              type: event.type,
              userId: event.userId,
            });
            if (workout) {
              workoutName = this.getWorkoutName(event, workout);
            } else {
              workoutName = "Workout not found";
            }
          }
          const eventDate = event.createdAt?.toDate 
            ? event.createdAt.toDate() 
            : (event.createdAt || new Date());
          return `${this.getUserName(event)},${labelMap[event.eventType] || event.eventType},${
            event.type ?? ""
          },${eventDate},${workoutName}`;
        })
      );
      const blob = new Blob([csvData.join("\n")], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `events_${this.tableDateRange.startDate.toLocaleDateString()}_${this.tableDateRange.endDate.toLocaleDateString()}.csv`;
      a.click();
    },
  },
  computed: {
    filteredEvents() {
      if (!this.searchQuery) {
        return this.originalEvents;
      }

      const query = this.searchQuery.toLowerCase().trim();
      return this.originalEvents.filter((item) => {
        if (!item.user) return false;

        const searchableFields = [
          item.user.displayName,
          item.user.nickname,
          item.user.firstName,
          item.user.lastName,
          item.user.email,
        ].filter(Boolean);

        return searchableFields.some((field) =>
          field.toLowerCase().includes(query)
        );
      });
    },
    paginatedEvents() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      const paginated = this.filteredEvents.slice(start, end);
      this.hasMorePages = this.filteredEvents.length > end;
      return paginated;
    },
    totalPages() {
      return Math.ceil(this.filteredEvents.length / this.itemsPerPage) || 1;
    },
  },
  watch: {
    filteredEvents: {
      handler(newVal) {
        this.inAppEvents = this.paginatedEvents;
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.table-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px;
}

.search-container {
  margin-right: auto;
  flex-grow: 1;
  max-width: 100%;
}

.search-input {
  width: 100%;
  margin: 0;
}

.search-input ::v-deep .v-input__slot {
  margin: 0;
  margin-bottom: -2px !important;
}

.search-input ::v-deep .v-text-field__details {
  display: none;
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
}

.table-date-range {
  font-weight: bold;
  font-size: 12px;
  color: #3a3a3a;
  text-align: center;
  border-width: 1px;
  border-style: solid;
  border-color: #3a3a3a;
  border-radius: 8px;
  padding: 0px 8px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.table-date-range:hover {
  cursor: pointer;
}

.refresh-button {
  background-color: #ed702d !important;
  color: white !important;
  transition: transform 0.2s ease-in-out;
}
.refresh-button:hover {
  transform: rotate(180deg);
}
.pagination-button {
  background-color: #ed702d !important;
  color: white !important;
  transition: all 0.2s ease-in-out;
}
.pagination-button:hover:not(:disabled) {
  transform: scale(1.1);
}
.pagination-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.page-info {
  font-weight: bold;
  color: #3a3a3a;
  min-width: 60px;
  text-align: center;
}
.event-type-select,
.workout-type-select {
  max-width: 200px;
  padding: 8px;
  border: 1px solid #3a3a3a;
  border-radius: 4px;
  color: #3a3a3a;
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

.event-type-select:hover,
.workout-type-select:hover {
  border-color: #ed702d;
}

.event-type-select:focus,
.workout-type-select:focus {
  border-color: #ed702d;
  box-shadow: 0 0 0 1px #ed702d;
}
.table-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 16px;
  font-weight: bold;
  font-size: 16px;
  color: #3a3a3a;
  border-bottom-width: 1px;
  border-bottom-style: solid;
  border-bottom-color: #e4e4e4;
}

.events-count {
  color: #666;
  font-size: 14px;
  font-weight: normal;
}

.event-type-counts {
  display: flex;
  gap: 24px;
  padding: 8px 16px;
  border-bottom: 1px solid #e4e4e4;
  background-color: #fafafa;
}

.workout-type-counts {
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: center;
}

.workout-type-count {
  display: flex;
  gap: 8px;
  font-size: 14px;
}

.count-label {
  color: #666;
  font-size: 14px;
}

.count-value {
  font-weight: bold;
  color: #3a3a3a;
  font-size: 14px;
}

.download-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  font-weight: bold;
  color: #3a3a3a;
  transition: all 0.2s ease-in-out;
  opacity: 0.8;
}
.download-icon-2 {
  font-size: 32px;
  margin-right: 4px;
}
.download-icon-container:hover {
  cursor: pointer;
  transform: scale(1.1);
  opacity: 1;
}
</style> 